import { Link, useLocation } from "react-router-dom";
import classes from "../components/loggedout/LoggedOut.module.scss";
import { useForm } from "react-hook-form";
import { useTranslation } from 'react-i18next';
import { DDD } from "../lib/ddd/ddd";
import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import LoadingDots from "../components/UI/LoadingDots";
import { ThemeContext } from "../store/ThemeContext";
import { INPUT_MAX_LENGTH } from "../constants";
import axios from "axios";
import dddLogo from "../assets/logo.svg";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LanguageSelector from "../components/UI/LanguageSelector";
import { useLoggedOutHeading } from "../store/LoggedOutHeaderContext";

const apiRequestAddress = process.env.REACT_APP_API_ENDPOINT || window.location.origin + "/json.rpc";
const confirmEmailEndpoint = process.env.REACT_APP_API_ENDPOINT ? process.env.REACT_APP_ORIGIN+ "/auth/confirmuserproperty/email" : window.location.origin + "/auth/confirmuserproperty/email";

const Login = () => {
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [logindisabled, setLogindisabled] = useState(true);
    const [showTurnstile, setShowTurnstile] = useState(true);
    const [showPassword, setShowPassword] = useState(false); // cosmetic only — does not touch submit/validation logic
    const {logo} = useContext(ThemeContext);
    const { t, i18n } = useTranslation();
    const isInitializedRef = useRef(false);
    const turnstileRef = useRef(null);
    const splitRef = useRef(null);
    const headingRef = useRef(null);
    const googleBtnRef = useRef(null);
    const logoRowRef = useRef(null);
    const emailLabelRef = useRef(null);
    const infoBtnRef = useRef(null);
    const [infoRowTops, setInfoRowTops] = useState(null);
    const location = useLocation();
    const confirmemail = location.state?.confirmemail;
    useLoggedOutHeading(confirmemail ? "" : t("Welcome back"));

    const {
        register,
        reset,
        formState: { errors },
        handleSubmit,
        control,
        setValue,
    } = useForm();

    // Pins anchor points in the left panel to their counterparts on the right: the logo
    // group's top to the "Welcome back" heading, "Don't have an account?" to the "Email
    // Address" label, and the button to the Google button — matching the Figma reference.
    // The body paragraph is not independently pinned; it flows normally below "Don't have
    // an account?" with its own fixed Figma spacing (20px) and moves with it.
    useLayoutEffect(() => {
        const split = splitRef.current;
        if (!split) return;

        const updateInfoRowTops = () => {
            if ((window.innerWidth <= 767 || window.innerHeight <= 600)) {
                // Mobile uses a genuinely different stacked flow layout, not JS-positioned rows.
                setInfoRowTops(null);
                return;
            }
            const heading = headingRef.current;
            const googleBtn = googleBtnRef.current;
            const logoRow = logoRowRef.current;
            const emailLabel = emailLabelRef.current;
            if (!heading || !googleBtn) return;
            // offsetTop is layout size (unaffected by --login-scale's transform) measured from
            // the shared offsetParent (.loginFormPanel) — reused directly as the left panel's
            // `top` since .loginInfoPanel's padding-top matches .loginFormPanel's, so both
            // panels' padding edges start at the same Y.

            // "Don't have an account?" stays a normal flow child (so the body paragraph's own
            // margin-top below it keeps working) — instead of pinning its `top` directly, its
            // margin-top is computed so it lands a few px below emailLabel's offsetTop (a
            // deliberate small offset, requested after the exact match landed a hair tight):
            // target position minus where the logo row's bottom edge currently is (both
            // expressed relative to the shared .loginFormPanel coordinate space via
            // heading.offsetTop).
            const nudgeDown = 8;
            let noAccountMarginTop;
            if (logoRow && emailLabel) {
                const logoBottom = heading.offsetTop + logoRow.offsetTop + logoRow.offsetHeight;
                noAccountMarginTop = emailLabel.offsetTop - logoBottom + nudgeDown;
            }

            // .loginInfoBtn is position:absolute, so it doesn't naturally push the panel
            // taller the way a normal-flow element would — a translation running to more
            // lines than English (Polish/Slovenian/Croatian all measured 2-3 lines vs 1)
            // just grew past the card's bottom edge instead, clipped by .loginSplit's
            // overflow:hidden. Explicitly reserving enough panel height for the button's
            // own (already-wrapped) offsetHeight fixes that regardless of language.
            const btnTop = googleBtn.offsetTop;
            const panelMinHeight = infoBtnRef.current
                ? btnTop + infoBtnRef.current.offsetHeight + 40
                : undefined;

            setInfoRowTops({
                groupTop: heading.offsetTop,
                btn: btnTop,
                noAccountMarginTop,
                panelMinHeight,
            });
        };

        updateInfoRowTops();
        // The logo row's height depends on Poppins-Bold (used at its bigger 2.25rem size)
        // actually being loaded — this effect can run before the font finishes fetching,
        // measuring fallback-font metrics that understate the row's true height, so
        // re-measure once the real font is ready.
        if (document.fonts?.ready) {
            document.fonts.ready.then(updateInfoRowTops);
        }
        const ro = new ResizeObserver(updateInfoRowTops);
        ro.observe(split);
        window.addEventListener('resize', updateInfoRowTops);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', updateInfoRowTops);
        };
    }, []);

    const loginSubmit = (data) => {
        setError(null);
        const ddd = new DDD(apiRequestAddress, data.userName, data.password);
        setLoading(true);
        ddd.Login(data.userName, data.password).then((response) => {
            if(confirmemail){
                axios.post(confirmEmailEndpoint, {}, {withCredentials: true}).then(res =>{
                    ddd.InvalidateSessionExtensions().then(() => {
                        window.location = "@/dashboard";
                    });
                }).catch(err => {
                    if (err.response && err.response.headers) {
                        const errorDetails = err.response.headers['x-error-details'];
                        console.error('Error Details:', errorDetails);
                    } else {
                        console.error('No error details found in the response.');
                    }
                    window.location = "@/dashboard";
                });
            }else{
                window.location = "@/dashboard";
            }

        }).catch((error) => {
            setLoading(false);
            _setError(t("Invalid login credentials"));
        });
    };

    const _setError = (message) => {
        setError(message);
        setTimeout(() => {
            setError(null);
        }, 5000);
    }
    const onGoogleClick = () => {
        //window.location.href = "oauth/google?signupHandlerUrl=" + encodeURIComponent(oauthSignUpHandlerUrl) + "&signinHandlerUrl=" + encodeURIComponent(oauthSignInHandlerUrl);
        window.location.href = "@oauth/google?signupHandlerUrl=dashboard-sign-up&signinHandlerUrl=dashboard";
    }

    useEffect(() => {
        if (window.turnstile && !isInitializedRef.current) {
          window.turnstile.render(turnstileRef.current, {
            sitekey: "0x4AAAAAABBlj-20RQ9Ma24E",
            callback: function (token) {
                _setShowTurnstile(true);
                setLogindisabled(true);
                verifyToken(token);
            },
            "error-callback": function (error) {
                setLogindisabled(true);
                _setError(t("Failed CAPTCHA verification"));
            },
            "expire-callback": () => {
                _setShowTurnstile(true);
            },
          });
          isInitializedRef.current = true;
        }
    }, [showTurnstile]);
    
    const verifyToken = async (token) => {
        const ddd = new DDD(apiRequestAddress);
        ddd.Rpc_ExecuteExtension("EUeInvoices.TurnstileVerification", {"Token": token}).then(res => {
            if(res?.result?.success === true){
                setLogindisabled(false);
                _setShowTurnstile(false);
                return;
            } 
            _setError(t("Failed CAPTCHA verification"));
        })
    }

    const _setShowTurnstile = (val) =>{console.log(val)
        if(val) setShowTurnstile(true);
        if(!val) setTimeout(() => setShowTurnstile(false), 2000);
    }

    return (
        <div className={classes.loginScaleWrapper}>
        <div className={classes.loginSplit} ref={splitRef}>
            {!logo && <div className={classes.loginInfoPanel} style={infoRowTops?.panelMinHeight !== undefined ? {minHeight: infoRowTops.panelMinHeight} : undefined}>
                <div className={classes.loginInfoTopGroup} style={infoRowTops ? {top: infoRowTops.groupTop} : undefined}>
                    <div className={classes.loginInfoLogo} ref={logoRowRef}>
                        <img src={dddLogo} alt="" />
                        <span>DDD Invoices</span>
                    </div>
                    <div className={classes.loginInfoNoAccount} style={infoRowTops?.noAccountMarginTop !== undefined ? {marginTop: infoRowTops.noAccountMarginTop} : undefined}>
                        <span className={classes.loginInfoNoAccountText}>{t("Don't have an account?")}</span>
                    </div>
                    <div className={classes.loginInfoBody}>
                        <p>{t("Sign up and start FREE 30 day access. Test e-invoicing, fiscalization, archiving and plugins.")}</p>
                    </div>
                </div>
                <button type="button" ref={infoBtnRef} className={classes.loginInfoBtn} style={infoRowTops ? {top: infoRowTops.btn} : undefined} onClick={() => {window.location.href="@dashboard-sign-up";}}>{t("Start FREE")}</button>
            </div>}
            <div className={classes.loginFormPanel}>
                <div className={classes.loginFlagWrap} style={{display:"flex", alignItems:"center"}}>
                    <LanguageSelector imgWidth="16"/>
                </div>
                {!confirmemail && <h2 className={classes.loginHeading} ref={headingRef}>{t("Welcome back")}</h2>}
                {confirmemail && <div><span className="" style={{color:"rgb(0, 128, 0)"}}>{t("To confirm your email please log in")}</span></div>}
                <form onSubmit={handleSubmit(loginSubmit)}>
                    {/* The field feeds Auth.IdentifyUsername, and sign-up now asks for a
                        username separately from the e-mail, so an account's username is no
                        longer necessarily its address — labelling this "Email Address"
                        would send those users looking for credentials they don't have. */}
                    <label htmlFor="userName" ref={emailLabelRef}>{t("Username or Email Address")}</label>
                    <input name="userName" id="userName" className={classes.loginInput} placeholder={t("test@company.com")} maxLength={INPUT_MAX_LENGTH} {...register("userName", {required: t("This field is required")})}/>
                    {errors["userName"] && <div style={{color: "rgb(232, 73, 14)"}}>{errors["userName"].message}</div>}
                    <label htmlFor="password">{t("Password")}</label>
                    <div className={classes.loginPasswordWrap}>
                        <input name="password" id="password" className={classes.loginInput} placeholder={t("Your password")} maxLength={INPUT_MAX_LENGTH} type={showPassword ? "text" : "password"} {...register("password", {required: t("This field is required")})}/>
                        <span className={classes.loginPasswordIcon} onClick={() => setShowPassword(v => !v)}>
                            {!showPassword && <VisibilityIcon style={{fontSize:"1.1rem"}}/>}
                            {showPassword && <VisibilityOffIcon style={{fontSize:"1.1rem"}}/>}
                        </span>
                    </div>
                    {errors["password"] && <div style={{color: "rgb(232, 73, 14)", marginBottom:"1rem"}}>{errors["password"].message}</div>}
                    <div className={classes.errorSlot}>
                        {error && <div style={{color: "rgb(232, 73, 14)"}}>{error}</div>}
                    </div>
                    <div className={classes.loginRow}>
                        <label className={classes.loginRememberLabel}>
                            <input type="checkbox" defaultChecked/> {t("Remember me")}
                        </label>
                        <Link to="/resetpassword" className={`${classes["main-blue-link"]} ${classes.loginForgotLink}`}>{t("Forgot your password?")}</Link>
                    </div>
                    <div className={classes.turnstileSlot}>
                        {showTurnstile && <div className="cf-turnstile" ref={turnstileRef} data-theme="light"></div>}
                    </div>
                    {loading && <LoadingDots/>}
                    {!loading && <button type="submit" className={classes.loginSubmitBtn} disabled={logindisabled&&!process.env.REACT_APP_API_ENDPOINT}>{t("Log in")}</button>}

                </form>
                {!logo && <><div className={classes["line-through"]}>
                    <span className={classes["line-text"]}>{t("or")}</span>
                </div>
                <div onClick={onGoogleClick} className={classes.loginGoogleBtn} ref={googleBtnRef}>
                    <svg width="20" height="20" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_3429_249)">
                        <path d="M18.6749 9.71009C18.6749 8.93122 18.6131 8.36285 18.4792 7.77344H9.77344V11.2889H14.8835C14.7805 12.1625 14.2242 13.4782 12.9878 14.3623L12.9705 14.48L15.7231 16.6585L15.9138 16.6779C17.6652 15.0254 18.6749 12.594 18.6749 9.71009Z" fill="#4285F4"/>
                        <path d="M9.77165 18.9724C12.2752 18.9724 14.3769 18.1303 15.912 16.6778L12.9861 14.3622C12.2031 14.9201 11.1522 15.3095 9.77165 15.3095C7.31963 15.3095 5.23851 13.6571 4.49664 11.373L4.3879 11.3825L1.52571 13.6454L1.48828 13.7517C3.01306 16.8462 6.14508 18.9724 9.77165 18.9724Z" fill="#34A853"/>
                        <path d="M4.4974 11.3731C4.30165 10.7837 4.18836 10.1521 4.18836 9.49955C4.18836 8.84693 4.30165 8.21543 4.4871 7.62602L4.48191 7.50049L1.58385 5.20117L1.48903 5.24725C0.860597 6.53136 0.5 7.97337 0.5 9.49955C0.5 11.0257 0.860597 12.4677 1.48903 13.7518L4.4974 11.3731Z" fill="#FBBC05"/>
                        <path d="M9.77165 3.69016C11.5128 3.69016 12.6873 4.45851 13.357 5.1006L15.9738 2.49029C14.3667 0.964108 12.2752 0.0273438 9.77165 0.0273438C6.14508 0.0273438 3.01306 2.15346 1.48828 5.2479L4.48635 7.62667C5.23851 5.34267 7.31963 3.69016 9.77165 3.69016Z" fill="#EB4335"/>
                        </g>
                        <defs>
                        <clipPath id="clip0_3429_249">
                        <rect x="0.5" width="18.1739" height="19" rx="9.08696" fill="white"/>
                        </clipPath>
                        </defs>
                    </svg>
                    <div className="fw-bold">{t("Sign in with Google")}</div>
                </div>
                </>}
                </div>
        </div>
        </div>
    );
}
export default Login;